import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useLanguage } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Sparkles, Upload, X, ArrowRight, ArrowLeft, Check, Loader2,
  Image as ImageIcon, Camera, Type, Plus, ExternalLink, PartyPopper, SkipForward
} from "lucide-react";

// Reuse client-side compression
const compressImage = (file, maxW = 1600, q = 0.85) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const r = Math.min(maxW / img.width, 1);
        const w = Math.round(img.width * r);
        const h = Math.round(img.height * r);
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        c.toBlob((b) => {
          if (!b) return reject(new Error("compress fail"));
          resolve(new File([b], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" }));
        }, "image/jpeg", q);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const uploadImage = async (file, endpoint, maxW = 1600) => {
  const compressed = await compressImage(file, maxW, 0.85);
  const fd = new FormData();
  fd.append("file", compressed);
  const r = await axios.post(`${API}/upload/${endpoint}`, fd, {
    withCredentials: true,
    headers: { "Content-Type": "multipart/form-data" }
  });
  return r.data.url;
};

const SmartPageOnboardingWizard = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [completed, setCompleted] = useState(false);
  const checkedRef = useRef(false);

  // Local form state
  const [logoUrl, setLogoUrl] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [aboutShort, setAboutShort] = useState("");
  const [gallery, setGallery] = useState([]);

  const logoInputRef = useRef();
  const coverInputRef = useRef();
  const galleryInputRef = useRef();

  // Detect: should we show the wizard?
  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/tenants/me`, { withCredentials: true });
        const t = r.data;
        setTenant(t);
        // Skip if user already dismissed/completed it
        if (t.smartpage_onboarding_dismissed) return;
        // Show if Smart Page is mostly empty (any of: no logo, no cover, no about, no gallery)
        const isIncomplete =
          !t.logo_url ||
          !t.cover_image ||
          !t.about_short ||
          !Array.isArray(t.gallery) || t.gallery.length === 0;
        if (isIncomplete) {
          // Pre-fill with whatever we have
          setLogoUrl(t.logo_url || "");
          setCoverUrl(t.cover_image || "");
          setAboutShort(t.about_short || "");
          setGallery(Array.isArray(t.gallery) ? t.gallery : []);
          // Tiny delay so user sees dashboard first
          setTimeout(() => setShow(true), 800);
        }
      } catch (e) { /* silent */ }
    })();
  }, []);

  if (!show && !completed) return null;

  // ---- Save / Dismiss handlers ----
  const persist = async (extra = {}) => {
    if (!tenant?.tenant_id) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/tenants/${tenant.tenant_id}`, {
        logo_url: logoUrl,
        cover_image: coverUrl,
        about_short: aboutShort,
        gallery,
        ...extra,
      }, { withCredentials: true });
    } catch (e) {
      toast.error(language === "it" ? "Errore salvataggio" : "Save error");
    } finally {
      setSaving(false);
    }
  };

  const dismissForever = async () => {
    await persist({ smartpage_onboarding_dismissed: true });
    setShow(false);
  };

  const finish = async () => {
    await persist({ smartpage_onboarding_dismissed: true });
    setCompleted(true);
  };

  // ---- Upload handlers ----
  const onLogoFile = async (file) => {
    if (!file) return;
    setSaving(true);
    try {
      const url = await uploadImage(file, "logo", 800);
      setLogoUrl(url);
      toast.success(language === "it" ? "Logo caricato!" : "Logo uploaded!");
    } catch (e) {
      toast.error(language === "it" ? "Errore upload" : "Upload error");
    } finally { setSaving(false); }
  };

  const onCoverFile = async (file) => {
    if (!file) return;
    setSaving(true);
    try {
      const url = await uploadImage(file, "cover", 1600);
      setCoverUrl(url);
      toast.success(language === "it" ? "Copertina caricata!" : "Cover uploaded!");
    } catch (e) {
      toast.error(language === "it" ? "Errore upload" : "Upload error");
    } finally { setSaving(false); }
  };

  const onGalleryFiles = async (files) => {
    if (!files || files.length === 0) return;
    setSaving(true);
    const newItems = [];
    for (const f of Array.from(files).slice(0, 6)) {
      try {
        const url = await uploadImage(f, "gallery", 1600);
        newItems.push({ id: `g_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, url });
      } catch (e) { /* skip */ }
    }
    if (newItems.length > 0) {
      setGallery((prev) => [...prev, ...newItems]);
      toast.success(language === "it" ? `${newItems.length} foto caricate` : `${newItems.length} photos uploaded`);
    }
    setSaving(false);
  };

  const removeGallery = (id) => setGallery((g) => g.filter((x) => x.id !== id));

  // ---- Steps definition ----
  const steps = [
    { key: "welcome", title: language === "it" ? "Crea la tua pagina perfetta" : "Build your perfect page", subtitle: language === "it" ? "3 minuti, zero stress." : "3 minutes, zero stress." },
    { key: "logo", title: language === "it" ? "Aggiungi il tuo logo" : "Add your logo", subtitle: language === "it" ? "Lo vedranno nella homepage e nelle email." : "Shown on homepage & emails.", icon: ImageIcon },
    { key: "cover", title: language === "it" ? "Scegli una copertina" : "Pick a cover image", subtitle: language === "it" ? "L'immagine che fa innamorare a primo sguardo." : "The image that creates the wow effect.", icon: Camera },
    { key: "slogan", title: language === "it" ? "Una frase che ti racconta" : "Tell us in one line", subtitle: language === "it" ? "Cosa ti rende speciale, in poche parole." : "What makes you special, briefly.", icon: Type },
    { key: "gallery", title: language === "it" ? "3-4 foto del tuo locale" : "3-4 photos of your place", subtitle: language === "it" ? "Le foto raccontano più di mille parole." : "Pictures speak louder than words.", icon: ImageIcon },
    { key: "done", title: language === "it" ? "Fatto! 🎉" : "Done! 🎉", subtitle: language === "it" ? "La tua Smart Page è pronta." : "Your Smart Page is ready." },
  ];
  const total = steps.length;
  const current = steps[step];
  const progress = ((step + 1) / total) * 100;

  // Final completed screen (after persist) — different layout
  if (completed) {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-md p-4 animate-in fade-in duration-300" data-testid="onboarding-success">
        <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-md w-full p-8 text-center shadow-2xl animate-in zoom-in-95 duration-300">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center mx-auto mb-5 shadow-xl">
            <PartyPopper className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold mb-2">{language === "it" ? "La tua Smart Page è online!" : "Your Smart Page is live!"}</h2>
          <p className="text-slate-500 dark:text-slate-400 mb-6 text-sm">
            {language === "it" ? "Condividila con i clienti e ricevi prenotazioni 24/7." : "Share it with clients and get bookings 24/7."}
          </p>
          <div className="flex flex-col gap-2">
            <Button
              onClick={() => { window.open(`/book/${tenant.slug}`, "_blank"); setCompleted(false); }}
              className="rounded-full py-6 font-semibold bg-gradient-to-r from-violet-600 to-pink-500 hover:opacity-95"
              data-testid="onboarding-open-page"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              {language === "it" ? "Apri la mia pagina" : "Open my page"}
            </Button>
            <Button
              onClick={() => { navigate("/smartpage"); setCompleted(false); }}
              variant="outline"
              className="rounded-full py-5"
              data-testid="onboarding-go-editor"
            >
              {language === "it" ? "Personalizza ancora" : "Customize more"}
            </Button>
            <button
              onClick={() => setCompleted(false)}
              className="text-xs text-slate-400 hover:text-slate-600 mt-2"
            >
              {language === "it" ? "Più tardi" : "Later"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const next = async () => {
    // Persist progress at each step
    await persist();
    if (step < total - 1) setStep(step + 1);
    else await finish();
  };

  const back = () => step > 0 && setStep(step - 1);

  const skipStep = () => {
    if (step < total - 1) setStep(step + 1);
    else finish();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/75 backdrop-blur-md p-0 sm:p-4 animate-in fade-in duration-300" data-testid="onboarding-wizard">
      <div className="w-full sm:max-w-lg bg-white dark:bg-slate-900 rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[95vh] flex flex-col animate-in slide-in-from-bottom sm:zoom-in-95 duration-300">
        {/* Top bar with progress + skip-forever */}
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="font-semibold text-sm">{language === "it" ? "Setup Smart Page" : "Smart Page Setup"}</span>
              <span className="text-xs text-slate-400 ml-1">· {step + 1}/{total}</span>
            </div>
            <button
              onClick={dismissForever}
              className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 underline-offset-2 hover:underline"
              data-testid="onboarding-dismiss-forever"
              title={language === "it" ? "Chiudi (puoi sempre tornare dall'editor)" : "Close (always available from editor)"}
            >
              {language === "it" ? "Salta tutto" : "Skip all"}
            </button>
          </div>
          <div className="h-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-violet-500 to-pink-500 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
              data-testid="onboarding-progress-bar"
            />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-7" data-testid={`onboarding-step-${current.key}`}>
          {/* Welcome */}
          {current.key === "welcome" && (
            <div className="text-center py-4">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-500 via-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-5 shadow-2xl shadow-violet-500/30">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold mb-2">{current.title}</h2>
              <p className="text-slate-500 dark:text-slate-400 mb-7">{current.subtitle}</p>
              <div className="grid grid-cols-3 gap-3 mb-6 text-center">
                <div className="p-3 rounded-2xl bg-slate-50 dark:bg-slate-800">
                  <ImageIcon className="w-5 h-5 mx-auto mb-1 text-violet-500" />
                  <p className="text-xs font-medium">Logo<br />+ Cover</p>
                </div>
                <div className="p-3 rounded-2xl bg-slate-50 dark:bg-slate-800">
                  <Type className="w-5 h-5 mx-auto mb-1 text-pink-500" />
                  <p className="text-xs font-medium">{language === "it" ? "Slogan" : "Tagline"}</p>
                </div>
                <div className="p-3 rounded-2xl bg-slate-50 dark:bg-slate-800">
                  <Camera className="w-5 h-5 mx-auto mb-1 text-amber-500" />
                  <p className="text-xs font-medium">{language === "it" ? "Galleria" : "Gallery"}</p>
                </div>
              </div>
              <p className="text-xs text-slate-400">
                {language === "it" ? "Puoi saltare ogni passo e tornare quando vuoi." : "You can skip any step and come back anytime."}
              </p>
            </div>
          )}

          {/* Logo */}
          {current.key === "logo" && (
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                  <ImageIcon className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{current.title}</h2>
                  <p className="text-xs text-slate-500">{current.subtitle}</p>
                </div>
              </div>
              {logoUrl ? (
                <div className="rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-6 flex items-center justify-center min-h-[200px] relative group">
                  <img src={logoUrl} alt="logo" className="max-h-32 max-w-full object-contain" data-testid="onboarding-logo-preview" />
                  <button onClick={() => setLogoUrl("")} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => logoInputRef.current?.click()}
                  className="w-full rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-violet-400 hover:bg-violet-50 dark:hover:bg-violet-950/20 p-8 transition-all"
                  data-testid="onboarding-logo-pick"
                >
                  {saving ? <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-violet-500" /> : <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />}
                  <p className="text-sm font-medium">{language === "it" ? "Clicca per caricare" : "Click to upload"}</p>
                  <p className="text-xs text-slate-400 mt-1">PNG, JPG · max 5MB</p>
                </button>
              )}
              <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => onLogoFile(e.target.files?.[0])} />
            </div>
          )}

          {/* Cover */}
          {current.key === "cover" && (
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center">
                  <Camera className="w-5 h-5 text-pink-600 dark:text-pink-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{current.title}</h2>
                  <p className="text-xs text-slate-500">{current.subtitle}</p>
                </div>
              </div>
              {coverUrl ? (
                <div className="rounded-2xl overflow-hidden bg-slate-100 h-44 relative group">
                  <img src={coverUrl} alt="cover" className="w-full h-full object-cover" data-testid="onboarding-cover-preview" />
                  <button onClick={() => setCoverUrl("")} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => coverInputRef.current?.click()}
                  className="w-full rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-pink-400 hover:bg-pink-50 dark:hover:bg-pink-950/20 p-8 transition-all"
                  data-testid="onboarding-cover-pick"
                >
                  {saving ? <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-pink-500" /> : <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />}
                  <p className="text-sm font-medium">{language === "it" ? "Clicca per caricare" : "Click to upload"}</p>
                  <p className="text-xs text-slate-400 mt-1">{language === "it" ? "Foto orizzontale · 1600×600 ideale" : "Wide image · 1600×600 ideal"}</p>
                </button>
              )}
              <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => onCoverFile(e.target.files?.[0])} />
            </div>
          )}

          {/* Slogan */}
          {current.key === "slogan" && (
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Type className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{current.title}</h2>
                  <p className="text-xs text-slate-500">{current.subtitle}</p>
                </div>
              </div>
              <Input
                value={aboutShort}
                onChange={(e) => setAboutShort(e.target.value.slice(0, 140))}
                placeholder={language === "it" ? "Es. Bellezza e relax nel cuore di Milano." : "e.g. Beauty and relax in the heart of the city."}
                className="rounded-xl text-base py-6"
                autoFocus
                data-testid="onboarding-slogan-input"
              />
              <div className="flex justify-between mt-2 text-xs text-slate-400">
                <span>{language === "it" ? "Suggerimento: parla del cliente, non di te." : "Tip: focus on the client, not yourself."}</span>
                <span>{aboutShort.length}/140</span>
              </div>
              {/* Templates */}
              <div className="mt-5">
                <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">{language === "it" ? "💡 Idee veloci" : "💡 Quick ideas"}</p>
                <div className="space-y-2">
                  {[
                    language === "it" ? "Stile, qualità e attenzione ai dettagli." : "Style, quality, attention to detail.",
                    language === "it" ? "La tua nuova esperienza di benessere." : "Your new wellness experience.",
                    language === "it" ? "Professionisti che fanno la differenza." : "Professionals who make a difference.",
                  ].map((tpl, i) => (
                    <button
                      key={i}
                      onClick={() => setAboutShort(tpl)}
                      className="w-full text-left p-3 rounded-xl bg-slate-50 dark:bg-slate-800 hover:bg-violet-50 dark:hover:bg-violet-950/30 text-sm transition-colors"
                      data-testid={`onboarding-template-${i}`}
                    >
                      {tpl}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Gallery */}
          {current.key === "gallery" && (
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <ImageIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{current.title}</h2>
                  <p className="text-xs text-slate-500">{current.subtitle}</p>
                </div>
              </div>
              {gallery.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {gallery.map((g) => (
                    <div key={g.id} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100 group" data-testid={`onboarding-gallery-${g.id}`}>
                      <img src={g.url} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => removeGallery(g.id)}
                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                onClick={() => galleryInputRef.current?.click()}
                className="w-full rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 p-6 transition-all"
                data-testid="onboarding-gallery-pick"
              >
                {saving ? <Loader2 className="w-7 h-7 mx-auto mb-1.5 animate-spin text-emerald-500" /> : <Plus className="w-7 h-7 mx-auto mb-1.5 text-slate-400" />}
                <p className="text-sm font-medium">{language === "it" ? "Aggiungi foto" : "Add photos"}</p>
                <p className="text-xs text-slate-400 mt-0.5">{language === "it" ? "Anche più foto insieme" : "Multiple at once"}</p>
              </button>
              <input ref={galleryInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onGalleryFiles(e.target.files)} />
              {gallery.length > 0 && gallery.length < 3 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-3 flex items-center gap-1">
                  💡 {language === "it" ? `Aggiungi almeno ${3 - gallery.length} foto in più per un effetto wow` : `Add ${3 - gallery.length} more photos for the wow effect`}
                </p>
              )}
            </div>
          )}

          {/* Done */}
          {current.key === "done" && (
            <div className="text-center py-3">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mx-auto mb-5 shadow-xl shadow-emerald-500/30">
                <Check className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold mb-2">{current.title}</h2>
              <p className="text-slate-500 dark:text-slate-400 mb-6">{current.subtitle}</p>
              {/* Mini preview card */}
              <div className="rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden mb-6 bg-slate-50 dark:bg-slate-800">
                {coverUrl && <img src={coverUrl} alt="" className="w-full h-28 object-cover" />}
                <div className="p-4 text-left flex items-center gap-3">
                  {logoUrl && <img src={logoUrl} alt="" className="w-12 h-12 rounded-lg object-contain bg-white p-1" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{tenant?.name}</p>
                    {aboutShort && <p className="text-xs text-slate-500 truncate">{aboutShort}</p>}
                  </div>
                </div>
                {gallery.length > 0 && (
                  <div className="grid grid-cols-3 gap-1 px-2 pb-2">
                    {gallery.slice(0, 3).map((g, i) => (
                      <img key={i} src={g.url} alt="" className="aspect-square object-cover rounded" />
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-400">
                {language === "it" ? "Puoi modificare tutto in qualsiasi momento dall'editor." : "You can edit everything anytime in the editor."}
              </p>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between gap-2">
          {step > 0 ? (
            <Button variant="outline" onClick={back} disabled={saving} className="rounded-full" data-testid="onboarding-back">
              <ArrowLeft className="w-4 h-4 mr-1" />
              {language === "it" ? "Indietro" : "Back"}
            </Button>
          ) : <div />}
          <div className="flex items-center gap-2">
            {step > 0 && step < total - 1 && (
              <button
                onClick={skipStep}
                className="text-xs text-slate-500 hover:text-slate-900 dark:hover:text-slate-200 px-2 py-1.5 flex items-center gap-1"
                data-testid="onboarding-skip-step"
              >
                <SkipForward className="w-3 h-3" />
                {language === "it" ? "Salta" : "Skip"}
              </button>
            )}
            <Button
              onClick={next}
              disabled={saving}
              className="rounded-full px-6 bg-gradient-to-r from-violet-600 to-pink-500 hover:opacity-95 font-semibold"
              data-testid="onboarding-next"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : current.key === "done" ? (
                <>{language === "it" ? "Pubblica" : "Publish"}<Sparkles className="w-4 h-4 ml-1" /></>
              ) : current.key === "welcome" ? (
                <>{language === "it" ? "Inizia" : "Start"}<ArrowRight className="w-4 h-4 ml-1" /></>
              ) : (
                <>{language === "it" ? "Avanti" : "Next"}<ArrowRight className="w-4 h-4 ml-1" /></>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartPageOnboardingWizard;
